import React from 'react';
import {
  BarChart3,
  Car,
  CheckCircle2,
  Clock,
  Flame,
  Megaphone,
  Trophy,
  Users,
  XCircle,
} from 'lucide-react';
import type { RoutyWeekReportArtifact } from '../../business/routy/routyArtifacts';
import { formatDurationMinutes } from '../../utils/timeUtils';

/**
 * The company week/period report card — the "CEO view" a manager gets when
 * asking Routy to summarize the team's week: company totals, highlights
 * (busiest day, most driving), one row per team member (appointments,
 * service mix, lead sources, drive miles), and company-wide lead sources.
 */
const WeekReportCard: React.FC<{ artifact: RoutyWeekReportArtifact }> = ({ artifact }) => {
  const { totals, highlights } = artifact;
  const stats: Array<{ label: string; value: string; className: string }> = [
    { label: 'Appointments', value: String(totals.appointments), className: 'text-ink-900' },
    ...(totals.completed > 0
      ? [{ label: 'Completed', value: String(totals.completed), className: 'text-emerald-600' }]
      : []),
    { label: 'Scheduled', value: String(totals.scheduled), className: 'text-blue-600' },
    ...(totals.cancelled + totals.noShow > 0
      ? [{ label: 'Cancelled / No-show', value: String(totals.cancelled + totals.noShow), className: 'text-red-500' }]
      : []),
    ...(totals.driveMiles != null
      ? [{ label: 'Drive miles', value: totals.driveMiles.toFixed(0), className: 'text-amber-600' }]
      : []),
  ];

  const highlightChips: Array<{ icon: React.ReactNode; text: string }> = [
    ...(artifact.busiestDay
      ? [
          {
            icon: <Flame className="h-3 w-3" />,
            text: `Busiest day: ${artifact.busiestDay.label} (${artifact.busiestDay.count})`,
          },
        ]
      : []),
    ...(highlights?.mostAppointments && artifact.members.length > 1
      ? [
          {
            icon: <Trophy className="h-3 w-3" />,
            text: `Most appointments: ${highlights.mostAppointments.name} (${highlights.mostAppointments.count})`,
          },
        ]
      : []),
    ...(highlights?.mostDriving && artifact.members.length > 1
      ? [
          {
            icon: <Car className="h-3 w-3" />,
            text: `Most driving: ${highlights.mostDriving.name} (${highlights.mostDriving.miles.toFixed(0)} mi)`,
          },
        ]
      : []),
  ];

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden max-w-md">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-blue-600" />
        <span className="text-sm font-semibold text-ink-900 truncate">Team Report · {artifact.rangeLabel}</span>
        <span className="ml-auto flex-shrink-0 rounded-md bg-ink-100 text-ink-500 text-2xs font-semibold px-2 py-0.5 uppercase tracking-wide">
          Company
        </span>
      </div>

      <div className="p-3 space-y-3">
        {/* Company totals */}
        <div className={`grid gap-2 ${stats.length >= 4 ? 'grid-cols-4' : 'grid-cols-3'}`}>
          {stats.slice(0, 4).map((s) => (
            <div key={s.label} className="rounded-lg border border-ink-100 bg-ink-50/60 px-2 py-2 text-center">
              <p className={`text-lg font-bold leading-none ${s.className}`}>{s.value}</p>
              <p className="mt-1 text-2xs text-ink-400 leading-none">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Highlights */}
        {highlightChips.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {highlightChips.map((h) => (
              <span
                key={h.text}
                className="inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200/70 text-amber-700 text-2xs font-medium px-2 py-1"
              >
                {h.icon}
                {h.text}
              </span>
            ))}
          </div>
        )}

        {/* Per-member rows */}
        {artifact.members.length > 0 && (
          <div>
            <p className="inline-flex items-center gap-1.5 text-2xs font-semibold text-ink-400 uppercase tracking-wider mb-1.5">
              <Users className="h-3.5 w-3.5" />
              Team
            </p>
            <div className="space-y-1.5">
              {artifact.members.map((m) => (
                <div key={m.name} className="rounded-lg border border-ink-100 bg-surface px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-ink-800 truncate">{m.name}</p>
                    <span className="flex-shrink-0 inline-flex items-center gap-2 text-2xs">
                      <span className="font-bold text-ink-900">{m.total}</span>
                      {m.completed > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-emerald-600 font-semibold">
                          <CheckCircle2 className="h-3 w-3" />
                          {m.completed}
                        </span>
                      )}
                      {m.scheduled > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-blue-600 font-semibold">
                          <Clock className="h-3 w-3" />
                          {m.scheduled}
                        </span>
                      )}
                      {m.cancelled + m.noShow > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-red-500 font-semibold">
                          <XCircle className="h-3 w-3" />
                          {m.cancelled + m.noShow}
                        </span>
                      )}
                    </span>
                  </div>
                  {m.servicesLabel && (
                    <p className="mt-0.5 text-2xs text-ink-500 truncate">{m.servicesLabel}</p>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {m.driveMiles != null && m.driveMiles > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-md bg-ink-50 border border-ink-100 text-ink-500 text-2xs font-medium px-1.5 py-0.5">
                        <Car className="h-3 w-3" />
                        {m.driveMiles.toFixed(0)} mi
                        {m.driveMinutes != null && m.driveMinutes > 0 && (
                          <span className="text-ink-400">· {formatDurationMinutes(m.driveMinutes)}</span>
                        )}
                      </span>
                    )}
                    {(m.leadSources ?? []).map((l) => (
                      <span
                        key={l.source}
                        className="inline-flex items-center gap-1 rounded-md bg-blue-50 border border-blue-200/70 text-blue-700 text-2xs font-medium px-1.5 py-0.5"
                      >
                        {l.source}
                        <span className="font-bold">{l.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Company-wide lead sources */}
        {artifact.leadSources && artifact.leadSources.length > 0 && (
          <div>
            <p className="inline-flex items-center gap-1.5 text-2xs font-semibold text-ink-400 uppercase tracking-wider mb-1.5">
              <Megaphone className="h-3.5 w-3.5" />
              Lead sources
            </p>
            <div className="flex flex-wrap gap-1.5">
              {artifact.leadSources.map((l) => (
                <span
                  key={l.source}
                  className="inline-flex items-center gap-1 rounded-md bg-blue-50 border border-blue-200/70 text-blue-700 text-2xs font-medium px-2 py-1"
                >
                  {l.source}
                  <span className="font-bold">{l.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeekReportCard;
